/**
 * `@atlas-nook/brand` — the constants every artifact is compiled with.
 *
 * Project identity, region, database and codebase names, hosting sites, and the palette.
 * Compiled into the functions bundle, the Cloud Run image, and every site, so a value that
 * drifts here drifts everywhere at once — which is the point.
 *
 * **No API keys and no secrets.** The Firebase *web* config is public but per-environment,
 * so it is read from `VITE_FIREBASE_*` in the app rather than hardcoded here.
 */
export const PACKAGE_NAME = '@atlas-nook/brand';
// ─── Project ─────────────────────────────────────────────────────────────────
/** One shared Firebase project; the consumer platform lives on it too (PRD 05 §7). */
export const PROJECT_ID = 'atlas-nook';
/** Frankfurt, for Firestore, Storage, Functions, and Cloud Run alike. */
export const REGION = 'europe-west3';
/**
 * The Studio's **named** database. The platform stays on `(default)`, each database carries
 * its own rules, and neither repo can clobber the other. The cost is load-bearing: rules
 * cannot `get()` across databases, so anything shared is bridged by a Cloud Function.
 */
export const FIRESTORE_DATABASE = 'studio';
/**
 * The Studio's functions codebase. It must stay distinct from the platform's `default`:
 * `firebase deploy --only functions` deletes functions absent from the source codebase, so
 * two repos on one codebase delete each other's work on every deploy. Names take no dashes.
 */
export const FUNCTIONS_CODEBASE = 'studio';
export const STORAGE_BUCKET = 'atlas-nook.firebasestorage.app';
// ─── Hosting ─────────────────────────────────────────────────────────────────
/**
 * Firebase Hosting is **dev/preview only** — throwaway `.web.app` channels. Production is
 * served from Cloudflare on the real domains.
 */
export const HOSTING_SITES = Object.freeze({
    marketing: 'atlasnook-marketing',
    studio: 'atlasnook-studio',
    admin: 'atlasnook-admin',
});
/**
 * The real domains, served from Cloudflare Pages (PRD 01 §6).
 *
 * **One registrable domain, three hosts.** The store takes the apex — a person who hears
 * "Atlas Nook" and types `atlasnook.studio` should land on the shelf, not on a tool they
 * have no account for — and the two signed-in surfaces are subdomains of it. That closes
 * PRD 06 §1's open decision: the store is not on `atlasnook.store`, `atlasnook.art` or a
 * `patara.` subdomain of the platform, it is the apex here.
 *
 * `patara` is what the store builds its canonical, `hreflang` and sitemap URLs from, and
 * `studio` is what the `requestAuthMail` callable builds every sign-in `continueUrl` from
 * (`apps/studio/firebase/functions/src/config.ts`). Both follow this constant, so moving a
 * host is this line, a rebuild, and the Pages custom domain — nothing else in the repo.
 *
 * `platform` is the consumer platform's, and is not ours to serve.
 */
export const DOMAINS = Object.freeze({
    platform: 'atlasnook.org',
    patara: 'atlasnook.studio',
    studio: 'app.atlasnook.studio',
    admin: 'admin.atlasnook.studio',
});
/**
 * The Cloudflare Pages projects, one per app, keyed the way `deploy:sites` names them.
 *
 * They are **not** `HOSTING_SITES`: those are Firebase Hosting sites, which are dev/preview
 * `.web.app` channels and a different system entirely. Two of the names coincide because
 * both systems host the same app; nothing derives one from the other.
 *
 * Each app's `wrangler.toml` carries the same name for a person reading it, and
 * `scripts/vault/template.mjs` reads it from there — see that file for why the TOML is the
 * copy a human edits.
 */
export const PAGES_PROJECTS = Object.freeze({
    patara: 'atlasnook-patara',
    studio: 'atlasnook-studio',
    admin: 'atlasnook-admin',
});
// ─── Mail ────────────────────────────────────────────────────────────────────
/**
 * The envelope every transactional mail goes out in (PRD 05 §10, `general/08`). Three mails
 * exist — the sign-in link, verify, reset — and all three come from this address, through
 * Resend, from codebase `studio`. The sending key is `RESEND_API_KEY` in Secret Manager and
 * is **not** here: this package holds no secrets.
 */
export const MAIL = Object.freeze({
    from: `Atlas Nook <hello@${DOMAINS.platform}>`,
    replyTo: `hello@${DOMAINS.platform}`,
});
// ─── Theme ───────────────────────────────────────────────────────────────────
/** Re-exported so a surface needs one import for "the theme everything is in". */
export { DEFAULT_THEME_ID } from '@atlas-nook/story';
/**
 * The **Handcrafted Wonder** palette (PRD 01 §3) — the story-world look the Studio and
 * Patara wear. The platform's own app look (`@atlas-nook/foundation`) is a separate system.
 */
export const PALETTE = Object.freeze({
    background: '#F4EBDC',
    teal: '#3F6B6B',
    mustard: '#C9922E',
    spark: '#E4552B',
    ink: '#2E2A26',
});
/** The platform app look's primary, kept here so the two systems are named in one place. */
export const FOUNDATION_TEAL = '#3A7E7C';
// ─── Env-var naming ──────────────────────────────────────────────────────────
/**
 * The prefix for every server variable we name ourselves (e.g. `ATLAS_NOOK_FILM_RENDER_URL`).
 * Third-party-dictated names — `FAL_KEY`, `FUNCTIONS_EMULATOR`, `VITE_*` — keep their own.
 */
export const ENV_PREFIX = 'ATLAS_NOOK_';
//# sourceMappingURL=index.js.map